/**
 * EditorialBench — small editorial corrections to this BOOK's text, applied when it is spoken.
 *
 * ⭐ THE CHANGE LIST IS THE FEATURE, not the form above it. A respelling can be global because a
 * book-world name means one thing everywhere; a corrected PHRASE is ordinary prose, and prose
 * repeats. So nothing is saveable until a scan has run and the operator has ticked the occurrences
 * to change — per MATCH, not per line, because one line may hold the pattern three times and want
 * only the second changed.
 *
 * ⚠️ SCOPE IS THE OPPOSITE OF THE PRONUNCIATION PANEL'S, which sits beside it. That one edits the
 * whole Literary Group; this one edits ONE BOOK. An operator who has learned the first will assume
 * the second, so the panel says so out loud.
 *
 * ⚠️ THE BOOK IS NEVER CHANGED. The correction is applied to the string handed to the voice model, so
 * the text, its contentKey, and therefore every hand-made attribution survive — and removing an
 * entry restores the author's words with nothing to undo.
 *
 * ⛔ A REWRITE NEEDS A REASON. There are two acts here: restoring words the ingest damaged (a repair)
 * and changing words the author wrote (a rewrite). The author cannot be asked, so a rewrite is
 * recorded with why, and a delete keeps the record rather than erasing it.
 *
 * See docs/journal/EDITORIAL_TEXT_OVERLAY_SPEC.md.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Segment, Header, Icon, Input, Button, Message, Label, Table, Checkbox, Radio, Popup,
} from 'semantic-ui-react';
import { apiClient } from '../../apiTools/apiClient';
import ConfirmModal from '../prototype/ConfirmModal';
import LineTakes from './LineTakes';

const BLANK = { find: '', replace: '', kind: 'repair', reason: '', caseSensitive: false };

/** `{ "L1": Set(0,2) }` → the entry's targets, only for lines with something ticked. */
function targetsFromTicks(ticks, lines) {
  return lines
    .map((l) => ({
      lineId: l.lineId,
      contentKey: l.contentKey,
      occurrences: [...(ticks[l.lineId] || [])].sort((a, b) => a - b),
    }))
    .filter((t) => t.occurrences.length > 0);
}

/** One line's text with its matches marked, the ticked ones filled in. */
function Highlighted({ line, ticked }) {
  const out = [];
  let cursor = 0;
  line.matches.forEach((m) => {
    if (m.start > cursor) out.push(<span key={`t${cursor}`}>{line.text.slice(cursor, m.start)}</span>);
    const on = ticked.has(m.index);
    out.push(
      <span key={`m${m.index}`}
        title={on ? 'this occurrence will be changed' : 'not selected'}
        style={{
          background: on ? '#ffe9a8' : '#f2f2f2',
          border: `1px solid ${on ? '#e0b62c' : '#e0e0e0'}`,
          borderRadius: 3, padding: '0 0.15em', fontWeight: on ? 600 : 400,
        }}>
        <sup style={{ color: '#999', marginRight: 2 }}>{m.index + 1}</sup>{line.text.slice(m.start, m.end)}
      </span>,
    );
    cursor = m.end;
  });
  if (cursor < line.text.length) out.push(<span key="tail">{line.text.slice(cursor)}</span>);
  return <span style={{ lineHeight: 1.6 }}>{out}</span>;
}

const EditorialBench = ({ litGroupUuid, bookId, bookTitle, captured, onClearCapture }) => {
  const [entries, setEntries] = useState([]);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [draft, setDraft] = useState(BLANK);
  const [scan, setScan] = useState(null);          // { totalMatches, lines[] }
  const [ticks, setTicks] = useState({});          // lineId → Set(occurrenceIndex)
  const [impact, setImpact] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [confirmSave, setConfirmSave] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  // ⭐ UNDO WITHOUT SPENDING. The take that spoke the author's original words survives a corrected
  // re-render, so the fastest undo of a correction is restoring that take — reachable from the
  // correction itself, which is where an operator thinks to undo it.
  const [takesFor, setTakesFor] = useState(null);       // `${entryId}:${lineId}` or null

  const load = useCallback(async () => {
    if (!litGroupUuid || !bookId) return;
    setError(null);
    try {
      const r = await apiClient.get(`/litgroups/${litGroupUuid}/books/${bookId}/editorial`, {}, { includeDeleted: 1 });
      const all = r.data.entries || [];
      setEntries(all.filter((e) => !e.deletedAt));
      setHistory(all.filter((e) => e.deletedAt));
    } catch (e) {
      setError(e.response?.data?.error || 'Could not read the corrections');
    }
  }, [litGroupUuid, bookId]);

  useEffect(() => { load(); }, [load]);

  // A line captured in the Studio pre-fills the pattern, so the phrase is not retyped from a screen.
  useEffect(() => {
    if (captured && captured.text) setDraft((d) => ({ ...d, find: String(captured.text).trim().slice(0, 120) }));
  }, [captured]);

  const reset = () => {
    setDraft(BLANK); setScan(null); setTicks({}); setImpact(null); setPreview(null);
  };

  const tickedTargets = useMemo(
    () => (scan ? targetsFromTicks(ticks, scan.lines) : []),
    [ticks, scan],
  );
  const tickedCount = tickedTargets.reduce((n, t) => n + t.occurrences.length, 0);
  const entryFromDraft = () => ({ ...draft, targets: tickedTargets });

  const runScan = async () => {
    if (!draft.find.trim()) return;
    setScanning(true); setError(null); setMsg(null); setImpact(null); setPreview(null);
    try {
      const r = await apiClient.post(`/litgroups/${litGroupUuid}/books/${bookId}/editorial/scan`,
        { find: draft.find.trim(), caseSensitive: draft.caseSensitive });
      setScan(r.data);
      setTicks({});
      if (!r.data.totalMatches) setMsg('That text does not appear anywhere in this book.');
    } catch (e) {
      setError(e.response?.data?.error || 'Could not scan the book');
    } finally { setScanning(false); }
  };

  const toggleMatch = (lineId, index) => setTicks((prev) => {
    const next = { ...prev };
    const set = new Set(next[lineId] || []);
    if (set.has(index)) set.delete(index); else set.add(index);
    if (set.size) next[lineId] = set; else delete next[lineId];
    return next;
  });
  const tickLine = (line, on) => setTicks((prev) => {
    const next = { ...prev };
    if (on) next[line.lineId] = new Set(line.matches.map((m) => m.index));
    else delete next[line.lineId];
    return next;
  });
  const tickAll = (on) => setTicks(() => {
    if (!on || !scan) return {};
    const next = {};
    scan.lines.filter((l) => l.correctable).forEach((l) => { next[l.lineId] = new Set(l.matches.map((m) => m.index)); });
    return next;
  });

  /** Impact + preview together: what it costs, and what it will say. */
  const measure = async () => {
    setBusy(true); setError(null);
    try {
      const entry = entryFromDraft();
      const [i, p] = await Promise.all([
        apiClient.post(`/litgroups/${litGroupUuid}/books/${bookId}/editorial/impact`, { entry }),
        apiClient.post(`/litgroups/${litGroupUuid}/books/${bookId}/editorial/preview`, { entry }),
      ]);
      setImpact(i.data);
      setPreview(p.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not measure that correction');
    } finally { setBusy(false); }
  };

  const commit = async () => {
    setConfirmSave(false); setBusy(true); setError(null);
    try {
      // ⚠️ Wholesale, so the whole list is sent every time — read-modify-write, the rule the hyphen
      // verdicts taught: a partial save once discarded 45 decisions.
      const r = await apiClient.put(`/litgroups/${litGroupUuid}/books/${bookId}/editorial`,
        { entries: [...entries, entryFromDraft()] });
      if (r.data.rejected?.length) {
        setError(r.data.rejected.map((x) => x.reason).join(' '));
      } else {
        setMsg(`Saved. It applies the next time these lines are rendered — ${impact?.renderedLines || 0} already have audio.`);
        reset();
        if (onClearCapture) onClearCapture();
      }
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not save');
    } finally { setBusy(false); }
  };

  const remove = async (entry) => {
    setConfirmDelete(null); setBusy(true); setError(null);
    try {
      await apiClient.delete(`/litgroups/${litGroupUuid}/books/${bookId}/editorial/${entry.id}`);
      setMsg(`Removed. The author's words are spoken again from the next render; the record is kept below.`);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not remove that correction');
    } finally { setBusy(false); }
  };

  const restore = async (entry) => {
    setBusy(true); setError(null);
    try {
      await apiClient.post(`/litgroups/${litGroupUuid}/books/${bookId}/editorial/${entry.id}/restore`);
      setMsg('Restored.');
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not restore that correction');
    } finally { setBusy(false); }
  };

  if (!litGroupUuid || !bookId) return null;

  const canScan = draft.find.trim().length >= 4 && !scanning;
  const needsReason = draft.kind === 'rewrite' && !draft.reason.trim();
  const canSave = tickedCount > 0 && draft.replace.trim() && !needsReason && !busy;

  return (
    <Segment className="rs-editorial">
      <Header as="h5" style={{ marginBottom: '0.25em' }}>
        <Icon name="edit outline" /> Editorial corrections
      </Header>
      {/* ⚠️ Scope, and the opposite of the panel above it. */}
      <div style={{ fontSize: '0.82em', color: '#777', marginBottom: '0.75em' }}>
        Corrections for <strong>{bookTitle || 'this book'}</strong> only. They correct spoken dialog; the book
        itself is never edited, removing a correction restores the original words.
      </div>

      {error && <Message negative size="tiny" onDismiss={() => setError(null)}>{error}</Message>}
      {msg && <Message positive size="tiny" onDismiss={() => setMsg(null)}>{msg}</Message>}

      {/* ── 1. the draft ─────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '0.5em', alignItems: 'center', flexWrap: 'wrap' }}>
        <Input
          size="small" placeholder="Text as the book has it…" value={draft.find} disabled={busy}
          onChange={(_, { value }) => { setDraft((d) => ({ ...d, find: value })); setScan(null); setTicks({}); }}
          style={{ minWidth: 260, flex: 1 }}
        />
        <Icon name="arrow right" style={{ color: '#aaa' }} />
        <Input
          size="small" placeholder="Say it as…" value={draft.replace} disabled={busy}
          onChange={(_, { value }) => { setDraft((d) => ({ ...d, replace: value })); setImpact(null); setPreview(null); }}
          style={{ minWidth: 260, flex: 1 }}
        />
        <Popup
          size="tiny"
          content="Match capitals exactly. Off by default, so one correction covers a sentence-initial use too."
          trigger={
            <Checkbox label="Aa" checked={draft.caseSensitive} disabled={busy}
              onChange={() => { setDraft((d) => ({ ...d, caseSensitive: !d.caseSensitive })); setScan(null); }} />
          }
        />
        <Button size="small" primary basic disabled={!canScan} loading={scanning} onClick={runScan}>
          <Icon name="search" /> Find in book
        </Button>
      </div>

      {/* ⛔ The two acts, named. A rewrite changes the author's words and must say why. */}
      <div style={{ display: 'flex', gap: '1.2em', alignItems: 'center', marginTop: '0.6em', flexWrap: 'wrap' }}>
        <Radio label="Repair — the ingest damaged this" checked={draft.kind === 'repair'} disabled={busy}
          onChange={() => setDraft((d) => ({ ...d, kind: 'repair' }))} />
        <Radio label="Rewrite — the book says this, and it reads wrong" checked={draft.kind === 'rewrite'} disabled={busy}
          onChange={() => setDraft((d) => ({ ...d, kind: 'rewrite' }))} />
        {draft.kind === 'rewrite' && (
          <Input
            size="small" placeholder="Why — required for a rewrite" value={draft.reason} disabled={busy}
            onChange={(_, { value }) => setDraft((d) => ({ ...d, reason: value }))}
            style={{ minWidth: 300, flex: 1 }}
          />
        )}
      </div>

      {/* ── 2. the change list ───────────────────────────────────────────── */}
      {scan && scan.totalMatches > 0 && (
        <Segment secondary style={{ marginTop: '0.8em' }}>
          <div style={{ display: 'flex', gap: '0.6em', alignItems: 'center', marginBottom: '0.5em', flexWrap: 'wrap' }}>
            <strong>{scan.totalMatches}</strong> occurrence{scan.totalMatches === 1 ? '' : 's'} in{' '}
            <strong>{scan.lines.length}</strong> line{scan.lines.length === 1 ? '' : 's'}
            <Button size="mini" onClick={() => tickAll(true)}>Tick all</Button>
            <Button size="mini" onClick={() => tickAll(false)} disabled={!tickedCount}>None</Button>
            <span style={{ color: tickedCount ? '#2185d0' : '#999' }}>{tickedCount} selected</span>
          </div>
          <Table compact="very" celled unstackable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell collapsing>Ch</Table.HeaderCell>
                <Table.HeaderCell collapsing>Speaker</Table.HeaderCell>
                <Table.HeaderCell>Line — tick the occurrences to change</Table.HeaderCell>
                <Table.HeaderCell collapsing>Occurrences</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {scan.lines.map((l) => {
                const ticked = ticks[l.lineId] || new Set();
                return (
                  <Table.Row key={l.lineId} warning={!l.correctable}>
                    <Table.Cell collapsing>{l.chapterNumber ?? '—'}</Table.Cell>
                    <Table.Cell collapsing style={{ whiteSpace: 'nowrap' }}>
                      {l.speaker || <em>—</em>}
                      {/* A tick here costs audio: the line already has a take. */}
                      {l.rendered && (
                        <Popup size="tiny" content="This line already has audio — changing it needs a re-render and a re-stitch."
                          trigger={<Icon name="volume up" style={{ color: '#f2711c', marginLeft: 4 }} />} />
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <Highlighted line={l} ticked={ticked} />
                      {!l.correctable && (
                        <div style={{ fontSize: '0.8em', color: '#b58105', marginTop: 3 }}>
                          ⚠️ This line has no content key, so a correction cannot be anchored to it. Re-ingesting
                          the book gives it one.
                        </div>
                      )}
                    </Table.Cell>
                    <Table.Cell collapsing>
                      <div style={{ display: 'flex', gap: '0.35em', flexWrap: 'wrap' }}>
                        {l.matches.map((m) => (
                          <Checkbox
                            key={m.index}
                            label={`${m.index + 1}`}
                            checked={ticked.has(m.index)}
                            disabled={busy || !l.correctable}
                            onChange={() => toggleMatch(l.lineId, m.index)}
                          />
                        ))}
                        {l.matches.length > 1 && (
                          <Button size="mini" basic compact disabled={!l.correctable}
                            onClick={() => tickLine(l, ticked.size !== l.matches.length)}>
                            {ticked.size === l.matches.length ? 'none' : 'all'}
                          </Button>
                        )}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>

          <div style={{ display: 'flex', gap: '0.5em', alignItems: 'center', flexWrap: 'wrap' }}>
            <Button size="small" basic disabled={!tickedCount || busy} loading={busy} onClick={measure}>
              <Icon name="calculator" /> What will this change?
            </Button>
            <Button size="small" primary disabled={!canSave} onClick={() => setConfirmSave(true)}>
              <Icon name="save" /> Save correction
            </Button>
            {needsReason && <span style={{ color: '#b58105', fontSize: '0.85em' }}>A rewrite needs a reason.</span>}
          </div>

          {/* ── 3. impact + preview ──────────────────────────────────────── */}
          {impact && impact.known && (
            <Message size="tiny" info style={{ marginTop: '0.6em' }}>
              <Icon name="info circle" />
              Changes <strong>{impact.occurrences}</strong> occurrence{impact.occurrences === 1 ? '' : 's'} in{' '}
              <strong>{impact.lines}</strong> line{impact.lines === 1 ? '' : 's'}.{' '}
              {impact.renderedLines > 0
                ? <>⚠️ <strong>{impact.renderedLines}</strong> already have audio — about{' '}
                  <strong>{impact.charsToRerender.toLocaleString()}</strong> characters to re-render
                  {impact.chaptersToRestitch?.length
                    ? <>, and chapter{impact.chaptersToRestitch.length === 1 ? '' : 's'}{' '}
                      <strong>{impact.chaptersToRestitch.join(', ')}</strong> to re-stitch</>
                    : null}.</>
                : 'None of them has audio yet, so nothing needs re-rendering.'}
              {impact.takesOfUnknownText > 0 && (
                <div style={{ marginTop: 3 }}>
                  {impact.takesOfUnknownText} existing take{impact.takesOfUnknownText === 1 ? '' : 's'} predate
                  the record of what was spoken, so whether they say the old words cannot be told from here.
                </div>
              )}
            </Message>
          )}
          {preview && preview.known && (
            <div style={{ marginTop: '0.4em' }}>
              {preview.lines.filter((l) => l.changed).map((l) => (
                <div key={l.lineId} style={{ fontSize: '0.85em', marginBottom: '0.4em' }}>
                  <div style={{ color: '#999' }}>ch{l.chapterNumber} · {l.speaker || '—'} · as the model will hear it:</div>
                  <div style={{ color: '#a33' }}>− {l.before}</div>
                  <div style={{ color: '#1a7f37' }}>+ {l.after}</div>
                </div>
              ))}
            </div>
          )}
        </Segment>
      )}

      {/* ── 4. saved corrections ─────────────────────────────────────────── */}
      {entries.length > 0 && (
        <Table compact="very" celled unstackable style={{ marginTop: '0.8em' }}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Correction</Table.HeaderCell>
              <Table.HeaderCell collapsing>Kind</Table.HeaderCell>
              <Table.HeaderCell collapsing>Where</Table.HeaderCell>
              <Table.HeaderCell collapsing />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {entries.map((e) => {
              const occ = (e.targets || []).reduce((n, t) => n + (t.occurrences || []).length, 0);
              return (
                <Table.Row key={e.id} negative={e.stale?.length > 0}>
                  <Table.Cell>
                    <span style={{ color: '#a33' }}>{e.find}</span>
                    <Icon name="arrow right" style={{ color: '#ccc', margin: '0 0.4em' }} />
                    <span style={{ color: '#1a7f37' }}>{e.replace}</span>
                    {e.reason && <div style={{ fontSize: '0.8em', color: '#888' }}>{e.reason}</div>}
                    {/* ⚠️ A stale correction applies NOTHING. Saying so is the whole point of storing
                        the contentKey it was ticked against. */}
                    {e.stale?.length > 0 && (
                      <div style={{ fontSize: '0.8em', color: '#b32424', marginTop: 3 }}>
                        ⚠️ Not being applied: {e.stale.map((s) => s.reason).join('; ')}. Re-scan and save it again.
                      </div>
                    )}
                  </Table.Cell>
                  <Table.Cell collapsing>
                    <Label size="mini" color={e.kind === 'rewrite' ? 'orange' : 'grey'}>{e.kind}</Label>
                  </Table.Cell>
                  <Table.Cell collapsing>
                    {occ} occ · {(e.targets || []).length} line{(e.targets || []).length === 1 ? '' : 's'}
                    {/* One row per affected line, each able to show its takes — restoring the take
                        that spoke the original words undoes the correction's AUDIO for nothing, where
                        re-rendering costs ElevenLabs characters for a file already in S3. */}
                    {(e.targets || []).map((t2) => {
                      const key = `${e.id}:${t2.lineId}`;
                      return (
                        <div key={key} style={{ marginTop: 2 }}>
                          <Button
                            size="mini" basic compact
                            onClick={() => setTakesFor((v) => (v === key ? null : key))}
                          >
                            <Icon name={takesFor === key ? 'caret down' : 'undo'} />
                            takes
                          </Button>
                          {takesFor === key && (
                            <LineTakes bookId={bookId} lineId={t2.lineId} />
                          )}
                        </div>
                      );
                    })}
                  </Table.Cell>
                  <Table.Cell collapsing>
                    <Button size="mini" basic color="red" icon="trash" disabled={busy}
                      onClick={() => setConfirmDelete(e)} />
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      )}

      {/* ── 5. history — the record a delete keeps ───────────────────────── */}
      {history.length > 0 && (
        <div style={{ marginTop: '0.6em' }}>
          <Button size="mini" basic compact onClick={() => setShowHistory((v) => !v)}>
            <Icon name={showHistory ? 'caret down' : 'caret right'} />
            {history.length} removed correction{history.length === 1 ? '' : 's'}
          </Button>
          {showHistory && (
            <div style={{ marginTop: '0.4em', fontSize: '0.85em' }}>
              {history.map((e) => (
                <div key={e.id} style={{ display: 'flex', gap: '0.5em', alignItems: 'baseline', color: '#888', padding: '0.2em 0' }}>
                  <Label size="mini" basic>{e.kind}</Label>
                  <span style={{ textDecoration: 'line-through' }}>{e.find} → {e.replace}</span>
                  {e.reason && <em>{e.reason}</em>}
                  <span>· removed {e.deletedAt ? new Date(e.deletedAt).toLocaleDateString() : ''}</span>
                  <Button size="mini" basic compact disabled={busy} onClick={() => restore(e)}>restore</Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <ConfirmModal
        open={confirmSave}
        title={draft.kind === 'rewrite' ? 'Change the author’s words?' : 'Save this repair?'}
        icon={draft.kind === 'rewrite' ? 'warning sign' : 'edit outline'}
        danger={draft.kind === 'rewrite'}
        items={[`${draft.find}  →  ${draft.replace}`]}
        note={draft.kind === 'rewrite'
          ? `${tickedCount} occurrence(s) in ${tickedTargets.length} line(s) will be SPOKEN differently from how the book is written. The book is not edited, and this is recorded with your reason.`
          : `${tickedCount} occurrence(s) in ${tickedTargets.length} line(s). The book is not edited — only what the narrator says.`}
        confirmLabel={draft.kind === 'rewrite' ? 'Change what is spoken' : 'Save'}
        loading={busy}
        onConfirm={commit}
        onCancel={() => setConfirmSave(false)}
      />

      <ConfirmModal
        open={!!confirmDelete}
        title="Remove this correction?"
        items={confirmDelete ? [`${confirmDelete.find}  →  ${confirmDelete.replace}`] : []}
        note="The author's original words will be spoken again from the next render. The correction is kept in the removed list, so it can be restored."
        confirmLabel="Remove"
        loading={busy}
        onConfirm={() => remove(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
      />
    </Segment>
  );
};

export default EditorialBench;
